import {
  AloftPrice,
  CraftsmanAdjustmentProps,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
  schPositiveInt,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectAdjustmentSettings, selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { applyRoundingAndThresholds } from './utils/applyRoundingAndThresholds.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateFireplaceAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  compID: string,
) => {
  const subjectImprovements = improvements.subject;
  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentification.subject;
  const compPropertyIdentification = propertyIdentification[compID];

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(depreciationRatios) ||
    isUndefined(subjectPropertyIdentification) ||
    isUndefined(compPropertyIdentification)
  ) {
    return undefined;
  }

  const depreciationRatio = depreciationRatios.subject;
  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) {
    return undefined;
  }

  const { fireplace } = subjectImprovements;
  const { fireplace: compFireplace } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;
  const {
    address: { PostalCode: compZipCode },
  } = compPropertyIdentification;

  if (isUndefined(zipCode) || isUndefined(compZipCode)) {
    return undefined;
  }

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  const absence = {
    count: schPositiveInt.parse(0),
    type: 'freestanding' as const,
    zipCode,
  };

  return adjCalculator.getFireplaceAdj({
    subject:
      isDefined(fireplace) && isDefined(fireplace.count) && fireplace.type
        ? {
            count: fireplace?.count,
            type: fireplace?.type,
            zipCode,
          }
        : absence,
    comp:
      isDefined(compFireplace) && isDefined(compFireplace.count) && compFireplace.type
        ? {
            count: compFireplace.count,
            type: compFireplace.type,
            zipCode: compZipCode,
          }
        : absence,
    depreciationRatio,
    compDepreciationRatio,
  }).adjustment;
};

const selectFireplaceAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'fireplace'),
    (state, id: string) => id,
  ],
  calculateFireplaceAdj,
);

export const selectFireplaceAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'fireplace'),
    selectCompSequence,
  ],
  (adjustmentVersion, improvements, propertyIdentification, depreciationRatios, compSequence) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateFireplaceAdj(
        adjustmentVersion,
        improvements,
        propertyIdentification,
        depreciationRatios,
        propID,
      );

      if (isDefined(adj)) acc[propID] = adj;

      return acc;
    }, {}),
);

const selectAppliedFireplaceAdj = createPriceSelector(
  [selectFireplaceAdj, selectAdjustmentSettings],
  (adj, adjSettings) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return applyRoundingAndThresholds(adj, 'fireplace', adjSettings);
  },
);

export const selectAppliedFireplaceAdjDisplay = createSelector(
  [selectAppliedFireplaceAdj],
  (adj) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);

export const selectAppliedFireplaceAdjsDisplay = createSelector(
  [selectFireplaceAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (fireplaceAdjs, adjSettings, manualAdjustmentsEntities) =>
    Object.keys(fireplaceAdjs).reduce(
      (acc: Record<string, { value: string; color: string }>, propID) => {
        const adj = fireplaceAdjs[propID];
        const manualAdjustment = manualAdjustmentsEntities[propID]?.fireplace;
        if (isDefined(manualAdjustment)) {
          const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
          acc[propID] = {
            value: getStringValueFromAdjustment(manualAdjustmentPrice),
            color: getColorFromAdjustment(manualAdjustmentPrice),
          };
        } else if (isDefined(adj)) {
          acc[propID] = {
            value: getStringValueFromAdjustment(
              applyRoundingAndThresholds(fireplaceAdjs[propID], 'fireplace', adjSettings),
            ),
            color: getColorFromAdjustment(fireplaceAdjs[propID]),
          };
        }
        return acc;
      },
      {},
    ),
);
